/**
 * NAD+ Product Configuration with Environment-based Product Bundle IDs
 * 
 * Returns product configurations with productBundleId values that differ
 * between staging and production environments.
 * 
 * Environment Detection:
 * - Uses VITE_CAREVALIDATE_API_URL to determine environment
 * - Staging: api-staging.care360-next.carevalidate.com
 * - Production: api.care360-next.carevalidate.com
 */

/**
 * Get NAD+ products configuration with environment-specific productBundleId
 * @returns {Object} Product catalog configuration
 */
export function getCvNadProductsConfig() {
  const isProduction = import.meta.env.VITE_CAREVALIDATE_API_URL?.includes('api.care360-next.carevalidate.com')
  
  return {
    product_5: {
      id: "product_5",
      name: "NAD+ 500mg Injection",
      display_name: "NAD+ 500mg",
      another_name: "NAD+ 500mg Injection",
      type: "injection",
      medication: "nad_500mg",
      isVisible: false,
      default: false,
      price: 99.00,
      productId: isProduction ? "PROD_NAD_500MG_ID" : "d02a381f-1780-4d7d-a998-dcd797867652",
      productBundleId: isProduction 
        ? "PROD_NAD_500MG_BUNDLE_ID" // Production bundle ID
        : "054eec59-0d7f-4608-b1dd-a038b56e63e5", // Staging bundle ID
      imageUrl: "/images/products/nad+product.png",
      imageUrl2: "/images/products/nad+product.png",
      description: "Starter dose, effective and affordable.",
      badge: "More Affordable",
      badgeIcon: "/images/funnel3/bmi-checkout/dollar-1.png",
      badgeColor: "#2FBB25",
      imageBg: "#E6F2F1",
      tagline: "Starter dose, effective and affordable.",
      patientCountMin: 800,
      patientCountMax: 1100,
      pricePlan: {
        monthly: {
          months: 1,
          price: 99.00,
          pricePerMonth: 99.00,
          regularPricePerMonth: 250.00,
          productId: isProduction ? "PROD_NAD_500MG_ID" : "d02a381f-1780-4d7d-a998-dcd797867652",
          productBundleId: isProduction 
            ? "PROD_NAD_500MG_MONTHLY_BUNDLE_ID" // Production bundle ID
            : "054eec59-0d7f-4608-b1dd-a038b56e63e5", // Staging bundle ID
          supplyLabel: "30 Day Supply"
        },
        quarterly: {
          months: 3,
          price: 249.00,
          pricePerMonth: 83.00,
          regularPricePerMonth: 250.00,
          productId: isProduction ? "PROD_NAD_500MG_ID" : "d02a381f-1780-4d7d-a998-dcd797867652",
          productBundleId: isProduction 
            ? "PROD_NAD_500MG_QUARTERLY_BUNDLE_ID" // Production bundle ID
            : "4b758a7d-b4ed-4c82-b73e-fc9114cf05c6", // Staging bundle ID
          buttonLabel: "Best Value",
          supplyLabel: "90 Day Supply"
        },
        sixMonth: {
          months: 6,
          price: 498.00,
          pricePerMonth: 83.00,
          regularPricePerMonth: 250.00,
          productId: isProduction ? "PROD_NAD_500MG_ID" : "d02a381f-1780-4d7d-a998-dcd797867652",
          productBundleId: isProduction 
            ? "PROD_NAD_500MG_SIX_MONTH_BUNDLE_ID" // Production bundle ID
            : "4b758a7d-b4ed-4c82-b73e-fc9114cf05c6", // Staging bundle ID
          supplyLabel: "6 Month Supply"
        },
        annual: {
          months: 12,
          price: 750.00,
          pricePerMonth: 125.00,
          regularPricePerMonth: 250.00,
          productId: isProduction ? "PROD_NAD_500MG_ID" : "d02a381f-1780-4d7d-a998-dcd797867652",
          productBundleId: isProduction 
            ? "PROD_NAD_500MG_ANNUAL_BUNDLE_ID" // Production bundle ID
            : "4b758a7d-b4ed-4c82-b73e-fc9114cf05c6", // Staging bundle ID
          supplyLabel: "12 Month Supply"
        }
      }
    },
    product_6: {
      id: "product_6",
      name: "NAD+ Injection",
      display_name: "NAD+",
      another_name: "NAD+ Injection",
      type: "injection",
      medication: "nad_1000mg",
      isVisible: true,
      default: false,
      price: 159.00,
      productId: isProduction ? "7f429298-fd13-4f29-906e-1bacae0f4132" : "cf407707-ecd2-45de-9b09-f5d9b9feb5fd",
      productBundleId: isProduction 
        ? "74cefe7e-4bb4-4fd6-8022-92b735658a47" // Production bundle ID
        : "234b4b15-af19-40dd-818e-4d160281a175", // Staging bundle ID
      imageUrl: "/images/products/nad+product.png",
      imageUrl2: "/images/products/nad+product.png",
      description: "Maximum dose for maximum impact.",
      badge: "Maximum Dose",
      badgeIcon: "/images/funnel3/bmi-checkout/charger.png",
      badgeColor: "#147FF1",
      imageBg: "#CBD5E1",
      tagline: "Maximum dose for maximum impact.",
      patientCountMin: 600,
      patientCountMax: 900,
      pricePlan: {
        monthly: {
          months: 1,
          price: 159.00,
          pricePerMonth: 159.00,
          regularPricePerMonth: 227.00,
          productId: isProduction ? "7f429298-fd13-4f29-906e-1bacae0f4132" : "cf407707-ecd2-45de-9b09-f5d9b9feb5fd",
          productBundleId: isProduction 
            ? "74cefe7e-4bb4-4fd6-8022-92b735658a47" // Production bundle ID
            : "234b4b15-af19-40dd-818e-4d160281a175", // Staging bundle ID
          supplyLabel: "30 Day Supply"
        },
        quarterly: {
          months: 3,
          price: 447.00,
          pricePerMonth: 149.00,
          regularPricePerMonth: 213.00,
          productId: isProduction ? "7f429298-fd13-4f29-906e-1bacae0f4132" : "cf407707-ecd2-45de-9b09-f5d9b9feb5fd",
          productBundleId: isProduction 
            ? "9c30711a-222f-4f1f-81a4-08a689357013" // Production bundle ID
            : "1b766ffa-94ac-43f4-827a-0dee72078b15", // Staging bundle ID
          buttonLabel: "Best Value",
          supplyLabel: "90 Day Supply"
        },
        sixMonth: {
          months: 6,
          price: 834.00,
          pricePerMonth: 139.00,
          regularPricePerMonth: 199.00,
          productId: isProduction ? "7f429298-fd13-4f29-906e-1bacae0f4132" : "cf407707-ecd2-45de-9b09-f5d9b9feb5fd",
          productBundleId: isProduction 
            ? "fd5ac7d5-cebf-469e-bd86-a42288bbe8a0" // Production bundle ID
            : "93b5f2a7-c09a-4ba3-91e3-249995ff21b8", // Staging bundle ID
          supplyLabel: "6 Month Supply"
        },
        annual: {
          months: 12,
          price: 1548.00,
          pricePerMonth: 129.00,
          regularPricePerMonth: 184.00,
          productId: isProduction ? "7f429298-fd13-4f29-906e-1bacae0f4132" : "cf407707-ecd2-45de-9b09-f5d9b9feb5fd",
          productBundleId: isProduction 
            ? "c7243718-8f39-4cb8-83e7-c468ef5dda80" // Production bundle ID
            : "b21e0821-ffc9-4de1-8fca-b5d2fee834b2", // Staging bundle ID
          supplyLabel: "12 Month Supply"
        }
      }
    }
  }
}

// Default export for backward compatibility with JSON import syntax
export default getCvNadProductsConfig()
